import React, { useMemo, useState } from 'react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ChartContainer, ChartConfig } from './ChartContainer';
import { ChartWrapper } from './ChartWrapper';
import { ChartTooltipContent } from './ChartTooltip';
import { ChartLegendContent } from './ChartLegend';
import { defaultPalette } from '../../lib/chartPalette';
import { calculateTickConfig } from '../../lib/chartTickConfig';
import { truncateLabel, calculateOptimalLabelLength } from '../../lib/chartLabelUtils';
import { sanitizeForCSSVar } from '../../lib/chartCssUtils';

export interface LineConfig {
  dataKey: string;
  stroke?: string;
  strokeWidth?: number;
  dot?: boolean;
  type?: 'monotone' | 'linear' | 'step' | 'stepBefore' | 'stepAfter';
  /** Assign to a Y-axis: 'left' (default) or 'right' for dual-axis charts */
  yAxisId?: 'left' | 'right';
}

export interface LineChartProps {
  data: any[];
  config?: ChartConfig;
  title?: string;
  description?: string;
  xAxisKey: string;
  lines?: LineConfig[];
  showGrid?: boolean;
  showTooltip?: boolean;
  showLegend?: boolean;
  height?: number;
  className?: string;
  tooltipFormatter?: (value: any) => string;
  xAxisFormatter?: (value: any) => string;
  yAxisFormatter?: (value: any) => string;
  /** Formatter for the right Y-axis when using dual-axis mode */
  rightYAxisFormatter?: (value: any) => string;
  /** Enable expand button to view chart in fullscreen dialog */
  expandable?: boolean;
  /** Custom height for the expanded chart in the dialog */
  expandedHeight?: number;
  /** @internal Set by ChartWrapper to indicate expanded view - shows all ticks, no rotation, longer labels */
  isExpanded?: boolean;
}

// Helper to auto-generate config and lines from data
const useChartDefaults = (
  data: any[],
  xAxisKey: string,
  providedConfig?: ChartConfig,
  providedLines?: LineConfig[]
) => {
  return useMemo(() => {
    if (!data || data.length === 0) {
      return { config: providedConfig || {}, lines: providedLines || [] };
    }

    // Get all keys from first data item, excluding xAxisKey
    const dataKeys = Object.keys(data[0]).filter((key) => key !== xAxisKey);

    // Use provided lines or auto-generate from data keys
    const lines = providedLines || dataKeys.map((key) => ({
      dataKey: key,
      type: 'monotone',
      stroke: undefined, // Will be set by CSS var
      strokeWidth: 2,
      dot: false
    } as LineConfig));

    // Use provided config or auto-generate with default (violet) palette colors
    const config = providedConfig || dataKeys.reduce((acc, key, index) => {
      acc[key] = {
        label: key.charAt(0).toUpperCase() + key.slice(1), // Capitalize first letter
        color: defaultPalette[index % defaultPalette.length],
      };
      return acc;
    }, {} as ChartConfig);

    return { config, lines };
  }, [data, xAxisKey, providedConfig, providedLines]);
};

export const LineChart: React.FC<LineChartProps> = ({
  data,
  config: providedConfig,
  title,
  description,
  xAxisKey,
  lines: providedLines,
  showGrid = true,
  showTooltip = true,
  showLegend = true,
  height = 350,
  className,
  tooltipFormatter,
  xAxisFormatter,
  yAxisFormatter,
  rightYAxisFormatter,
  expandable = true,
  expandedHeight = 500,
  isExpanded = false,
}) => {
  const { config, lines } = useChartDefaults(data, xAxisKey, providedConfig, providedLines);
  const hasDualAxis = lines.some((l) => l.yAxisId === 'right');
  const [hoveredDataKey, setHoveredDataKey] = useState<string | null>(null);

  // Calculate tick label configuration based on data length
  const tickConfig = useMemo(() => {
    // Get base responsive config (handles rotation based on data length)
    const baseConfig = calculateTickConfig(data.length, { top: 10, right: 20, left: 10, bottom: 5 });

    // In expanded view: keep rotation but force showing ALL ticks + add extra bottom space
    if (isExpanded) {
      const expandedConfig = {
        ...baseConfig,
        interval: 0,  // Force show all ticks (keep rotation from baseConfig)
        margin: {
          ...baseConfig.margin,
          bottom: baseConfig.margin.bottom + 20, // Extra space to prevent legend overlap
        },
      };
      return expandedConfig;
    }

    // Normal view: use responsive logic as-is
    return baseConfig;
  }, [data.length, isExpanded]);

  // Create x-axis formatter that truncates labels and applies custom formatting
  const xAxisFormatterWithTruncation = useMemo(() => {
    // In expanded view: allow longer labels (20 chars for better readability)
    const maxLength = isExpanded ? 20 : calculateOptimalLabelLength(data.length);
    return (value: any) => {
      // Apply custom formatter first if provided
      const formattedValue = xAxisFormatter ? xAxisFormatter(value) : value;
      // Then truncate to prevent overlap
      return truncateLabel(formattedValue, maxLength);
    };
  }, [data.length, xAxisFormatter, isExpanded]);

  const chartContent = (
    <ChartContainer config={config}>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsLineChart data={data} margin={tickConfig.margin}>
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="hsl(var(--border))"
              vertical={false}
            />
          )}
          <XAxis
            dataKey={xAxisKey}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={xAxisFormatterWithTruncation}
            className="text-xs"
            angle={tickConfig.angle}
            textAnchor={tickConfig.textAnchor}
            interval={tickConfig.interval}
            height={tickConfig.margin.bottom}
          />
          <YAxis
            yAxisId="left"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={yAxisFormatter}
            className="text-xs"
          />
          {hasDualAxis && (
            <YAxis
              yAxisId="right"
              orientation="right"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={rightYAxisFormatter}
              className="text-xs"
            />
          )}
          {showTooltip && (
            <Tooltip
              content={
                <ChartTooltipContent
                  valueFormatter={tooltipFormatter}
                  indicator="line"
                />
              }
              cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeWidth: 1 }}
            />
          )}
          {showLegend && (
            <Legend
              content={
                <ChartLegendContent
                  onMouseEnter={(dataKey) => setHoveredDataKey(dataKey)}
                  onMouseLeave={() => setHoveredDataKey(null)}
                />
              }
            />
          )}
          {lines.map((line) => (
            <Line
              key={line.dataKey}
              type={line.type || 'monotone'}
              dataKey={line.dataKey}
              name={config[line.dataKey]?.label || line.dataKey}
              stroke={line.stroke || `var(--color-${sanitizeForCSSVar(line.dataKey)})`}
              strokeWidth={line.strokeWidth || 2}
              dot={line.dot !== undefined ? line.dot : false}
              activeDot={{ r: 4 }}
              opacity={hoveredDataKey === null || hoveredDataKey === line.dataKey ? 1 : 0.3}
              yAxisId={line.yAxisId || 'left'}
            />
          ))}
        </RechartsLineChart>
      </ResponsiveContainer>
    </ChartContainer>
  );

  return (
    <ChartWrapper
      title={title}
      description={description}
      className={className}
      expandable={expandable}
      expandedHeight={expandedHeight}
      chartComponent={LineChart}
      chartProps={{
        data,
        config: providedConfig,
        xAxisKey,
        lines: providedLines,
        showGrid,
        showTooltip,
        showLegend,
        height,
        tooltipFormatter,
        xAxisFormatter,
        yAxisFormatter,
        rightYAxisFormatter,
        // isExpanded will be added by ChartWrapper in expanded view
      }}
    >
      {chartContent}
    </ChartWrapper>
  );
};
